import { singleton } from '#shared/singleton';
import { useApi, post } from '~/composables/useApi';
import { watch } from 'vue';

/**
 * Automatic session refresh timer to prevent session expiry
 */
export const useSessionRefresh = singleton(() => {
    const config = useRuntimeConfig();
    const { fetch: _fetch } = useApi();
    
    let refreshTimer: NodeJS.Timeout | null = null;
    let sessionStartTime: number | null = null;
    
    /**
     * Calculate when to refresh the session:
     * - At 90% of remaining session time, OR
     * - 4 seconds before expiry (whichever is earliest)
     */
    const calculateRefreshDelay = (sessionStartTime: number, sessionDurationSec: number): number => {
        const now = Date.now();
        const elapsed = (now - sessionStartTime) / 1000; // elapsed time in seconds
        const remaining = Math.max(0, sessionDurationSec - elapsed); // remaining time in seconds
        
        // Option 1: 90% of remaining time
        const ninetyPercentRemaining = remaining * 0.9;
        
        // Option 2: 4 seconds before expiry
        const fourSecondsBeforeExpiry = remaining - 4;
        
        // Choose the earliest (smallest positive value)
        let refreshDelay = Math.min(ninetyPercentRemaining, fourSecondsBeforeExpiry);
        
        // Ensure we don't refresh immediately or in the past
        refreshDelay = Math.max(refreshDelay, 0.1); // minimum 100ms delay
        
        console.log(`Session refresh calculation:`, {
            sessionDurationSec,
            elapsed: elapsed.toFixed(1),
            remaining: remaining.toFixed(1),
            ninetyPercentRemaining: ninetyPercentRemaining.toFixed(1),
            fourSecondsBeforeExpiry: fourSecondsBeforeExpiry.toFixed(1),
            refreshDelay: refreshDelay.toFixed(1)
        });
        
        return refreshDelay * 1000; // convert to milliseconds
    };
    
    /**
     * Refresh the session by making an authenticated API call
     * The server middleware will automatically refresh the session cookie
     */
    const refreshSession = async () => {
        try {
            console.log('🔄 Refreshing session...');

            // Call the dedicated session refresh endpoint using the post function
            await post('session/refresh', {});

            console.log('✅ Session refreshed successfully');
            
            // Reset the session start time and schedule next refresh
            sessionStartTime = Date.now();
            scheduleNextRefresh();
            
        } catch (error) {
            console.error('❌ Failed to refresh session:', error);
            
            // If refresh fails, the user might need to log in again
            // Clear the timer to prevent further attempts
            clearRefreshTimer();
            
            // The error will be handled by the global error handler
            // which should redirect to login if session is invalid
        }
    };
    
    /**
     * Schedule the next session refresh based on current session state
     */
    const scheduleNextRefresh = () => {
        // Clear any existing timer
        clearRefreshTimer();
        
        if (typeof window === 'undefined') {
            return;
        }
        
        // Check if user is authenticated by checking sessionStorage directly
        const userId = sessionStorage.getItem('id');
        if (!userId || parseInt(userId) <= 0) {
            return;
        }
        
        const sessionDurationSec = config.public.sessionAgeSec;
        
        if (!sessionStartTime) {
            sessionStartTime = Date.now();
        }
        
        const refreshDelay = calculateRefreshDelay(sessionStartTime, sessionDurationSec);
        
        console.log(`⏰ Scheduling session refresh in ${(refreshDelay / 1000).toFixed(1)} seconds`);
        
        refreshTimer = setTimeout(() => {
            refreshSession();
        }, refreshDelay);
    };
    
    /**
     * Start the automatic session refresh timer
     * Call this after successful login
     */
    const startRefreshTimer = () => {
        console.log('🚀 Starting automatic session refresh timer');
        sessionStartTime = Date.now();
        scheduleNextRefresh();
    };
    
    /**
     * Clear the refresh timer
     * Call this on logout or when session is no longer valid
     */
    const clearRefreshTimer = () => {
        if (refreshTimer) {
            console.log('🛑 Clearing session refresh timer');
            clearTimeout(refreshTimer);
            refreshTimer = null;
        }
        sessionStartTime = null;
    };
    
    /**
     * Resume the refresh timer after page refresh/reload
     * This calculates the remaining session time and schedules appropriately
     */
    const resumeRefreshTimer = () => {
        if (typeof window === 'undefined') {
            return;
        }
        
        // Check if user is authenticated by checking sessionStorage directly
        const userId = sessionStorage.getItem('id');
        if (!userId || parseInt(userId) <= 0) {
            return;
        }
        
        // Try to get session start time from storage
        const storedStartTime = sessionStorage.getItem('sessionStartTime');
        if (storedStartTime) {
            sessionStartTime = parseInt(storedStartTime, 10);
            
            // Check if session might have expired
            const sessionDurationSec = config.public.sessionAgeSec;
            const elapsed = (Date.now() - sessionStartTime) / 1000;
            
            if (elapsed >= sessionDurationSec) {
                console.log('⚠️ Session may have expired, clearing timer');
                clearRefreshTimer();
                return;
            }
            
            console.log('🔄 Resuming session refresh timer after page reload');
            scheduleNextRefresh();
        } else {
            // No stored start time, assume session just started
            console.log('🔄 No stored session start time, starting fresh timer');
            startRefreshTimer();
        }
    };
    
    /**
     * Store session start time in sessionStorage for persistence across page reloads
     */
    const persistSessionStartTime = () => {
        if (typeof window !== 'undefined' && sessionStartTime) {
            sessionStorage.setItem('sessionStartTime', sessionStartTime.toString());
        }
    };
    
    /**
     * Clear stored session start time
     */
    const clearPersistedSessionStartTime = () => {
        if (typeof window !== 'undefined') {
            sessionStorage.removeItem('sessionStartTime');
        }
    };
    
    // Update the persistence whenever sessionStartTime changes
    watch(() => sessionStartTime, (newStartTime) => {
        if (newStartTime) {
            persistSessionStartTime();
        } else {
            clearPersistedSessionStartTime();
        }
    });
    
    return {
        startRefreshTimer,
        clearRefreshTimer,
        resumeRefreshTimer,
        refreshSession,
        // Expose internal functions for testing
        __testing: {
            calculateRefreshDelay
        }
    };
});